/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import {
    bool,
    object,
} from 'prop-types';

import { useSelector } from 'react-redux';
import TwoColumn from './variants/TwoColumn';
import ThreeColumn from './variants/ThreeColumn';
import TwoColRedesignResponsive from './variants/TwoColRedesignResponsive';
import TwoColRedesignResponsiveGrid from './variants/TwoColRedesignResponsiveGrid';
import TwoColumnPmall from './variants/TwoColumnPmall';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import TwoColumnDonation from './variants/Donation';
import { getActiveABTest } from '../../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import { getDonationsCategory } from '../../../../../../state/ducks/App/App-Selectors';
import TwoColumnPLA from './variants/TwoColumnPLA';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import TwoColumnFoodPLA from './variants/TwoColumnFoodPLA';
import useSSRMediaQuery from '../../../../../helpers/hooks/useSSRMediaQuery';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';
import ThreeColumnPlaRedesign from './variants/ThreeColumnPlaRedesign';

const variantLib = {
    TwoColumn: 'TwoColumn',
    ThreeColumn: 'ThreeColumn',
    TwoColRedesignResponsive: 'TwoColRedesignResponsive',
    TwoColRedesignResponsiveGrid: 'TwoColRedesignResponsiveGrid',
    TwoColumnPmall: 'TwoColumnPmall',
    TwoColumnDonation: 'TwoColumnDonation',
    TwoColumnPLA: 'TwoColumnPLA ',
    TwoCloumnFoodPLA: 'TwoCloumnFoodPLA',
    ThreeColumnPla: 'ThreeColumnRedesignPLA',
};

/**
 * Home to render and coordinating logic for the different
 * Product Page variants
 *
 * documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
 */
const PDPVariantController = (props) => {
    const {
        components,
        product,
        isPdpRedesignEnabled,
        showProductTitleTop,
        isPdpRedesignShortEnabled,
        isPlaRedesignEnabled,
        upSell,
        selectedSku,
        isPalRedesignVariantB,
        isPlaPassportEnabled,
        isPassportAfterCta,
    } = props;
    const { featureFlags = {} } = useContext(CrossMerch);

    const isPlaPage = useIsPlaPage();
    const isDisplayChannel = useIsDisplayChannel();
    const floralPLARedesign = useSelector(getActiveABTest('is_floral_pla_redesign'));
    const donationsCategoryName = useSelector(getDonationsCategory);
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const device = useSSRMediaQuery();
    /**
     * Decide here on which variant to use.
     * Default to TwoColumn
     */
    let variant = variantLib.TwoColumn;
    if (isPlaPage || isDisplayChannel) {
        if (device === 'mobile') {
            variant = variantLib.TwoColumnPLA;
        } else if (floralPLARedesign === 'VARIANT' && device === 'desktop') {
            variant = variantLib.TwoColumnPLA;
        } else if (isPlaRedesignEnabled) {
            variant = variantLib.ThreeColumnPla;
        } else {
            variant = variantLib.ThreeColumn;
        }
    }
    if (featureFlags['is-pdp-v-2-enabled']) {
        variant = variantLib.TwoColRedesignResponsive;
    }

    if (featureFlags['is-pmall-alternate-description-location'] && (product.brand === 'personalizationmall' || product.brand === 'thingsremembered')) {
        variant = variantLib.TwoColumnPmall;
    }
    if (donationsCategoryName?.toLowerCase() === 'donations') {
        variant = variantLib.TwoColumnDonation;
    }
    if (isPlaPage && featureFlags['is-simplified-pdp'] && foodPlaRedesign === 'variant') {
        variant = variantLib.TwoCloumnFoodPLA;
    }

    if ((isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column' && !isPlaRedesignEnabled) {
        variant = variantLib.ThreeColumn;
    }
    if ((isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column' && isPlaRedesignEnabled) {
        variant = variantLib.ThreeColumnPla;
    }

    if (variant === variantLib.TwoColumnPmall) {
        return (
            <TwoColumnPmall
                components={components}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }

    if (variant === variantLib.ThreeColumn) {
        return (
            <ThreeColumn
                components={components}
                product={product}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }
    if (variant === variantLib.ThreeColumnPla) {
        return (
            <ThreeColumnPlaRedesign
                components={components}
                product={product}
                upSell={upSell}
                selectedSku={selectedSku}
                isPalRedesignVariantB={isPalRedesignVariantB}
                isPlaPassportEnabled={isPlaPassportEnabled}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }
    if (variant === variantLib.TwoCloumnFoodPLA) {
        return (
            <TwoColumnFoodPLA
                components={components}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }
    if (featureFlags['is-pdp-grid-enabled']) {
        return (
            <TwoColRedesignResponsiveGrid
                components={components}
                isPdpRedesignEnabled={isPdpRedesignEnabled}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }
    if (variant === variantLib.TwoColRedesignResponsive) {
        return (
            <TwoColRedesignResponsive
                components={components}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }

    if (variant === variantLib.TwoColumnDonation) {
        return (
            <TwoColumnDonation
                components={components}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }

    if (variant === variantLib.TwoColumnPLA) {
        return (
            <TwoColumnPLA
                components={components}
                floralPLARedesign={floralPLARedesign}
                isPassportAfterCta={isPassportAfterCta}
            />
        );
    }
    return (
        <TwoColumn
            components={components}
            isPdpRedesignEnabled={isPdpRedesignEnabled}
            showProductTitleTop={showProductTitleTop}
            isPdpRedesignShortEnabled={isPdpRedesignShortEnabled}
            isPassportAfterCta={isPassportAfterCta}
        />
    );
};

PDPVariantController.propTypes = {
    components: object.isRequired,
    product: object.isRequired,
    isPdpRedesignEnabled: bool,
    showProductTitleTop: bool,
    isPdpRedesignShortEnabled: bool,
    isPlaRedesignEnabled: bool,
    upSell: bool,
    selectedSku: object,
    isPalRedesignVariantB: bool,
    isPlaPassportEnabled: bool,
    isPassportAfterCta: bool,
};

PDPVariantController.defaultProps = {
    isPdpRedesignEnabled: false,
    showProductTitleTop: false,
    isPdpRedesignShortEnabled: false,
    isPlaRedesignEnabled: false,
    upSell: false,
    selectedSku: {},
    isPalRedesignVariantB: false,
    isPlaPassportEnabled: false,
    isPassportAfterCta: false,
};

export default PDPVariantController;
